import type { HeartRateEvent } from '@model/DeviceConfiguration';

export const parseHeartRateData = (hrData: unknown): HeartRateEvent[] => {
  if (!hrData) return [];

  if (Array.isArray(hrData)) {
    return hrData as HeartRateEvent[];
  }

  if (typeof hrData === 'string') {
    try {
      const parsed = JSON.parse(hrData);
      return Array.isArray(parsed) ? (parsed as HeartRateEvent[]) : [];
    } catch {
      return [];
    }
  }

  return [];
};

export const getRepairedOnlyEvents = (hrData: unknown): HeartRateEvent[] =>
  parseHeartRateData(hrData).filter(event => Boolean(event.repair));

export const hasEffectiveRepairData = (hrData: unknown): boolean =>
  getRepairedOnlyEvents(hrData).length > 0;

export const normalizeTimestampToSecond = (timestamp: number) =>
  Math.floor(timestamp / 1000) * 1000;

export const getSessionTrackOriginTimestamp = (
  events: HeartRateEvent[]
): number | null => {
  const sorted = events
    .filter(event => typeof event.timestamp === 'number')
    .sort((left, right) => left.timestamp - right.timestamp);

  if (sorted.length === 0) return null;
  return normalizeTimestampToSecond(sorted[0].timestamp);
};

export const eventToRelativeSecond = (
  eventTimestamp: number,
  originTimestamp: number
) => Math.floor((eventTimestamp - originTimestamp) / 1000);

export type TrendSegmentKey = 'pre' | 'post';

export const buildTrendSegmentValueMap = (
  events: HeartRateEvent[],
  readValue: (event: HeartRateEvent) => number | undefined,
  segment: TrendSegmentKey,
  baselineWindowSeconds: number
): Map<number, number> => {
  if (events.length === 0) return new Map();

  const sortedEvents = [...events].sort(
    (left, right) => left.timestamp - right.timestamp
  );
  const originTimestamp = getSessionTrackOriginTimestamp(sortedEvents);
  if (originTimestamp == null) return new Map();

  const groupedByFullSecond = new Map<number, number[]>();

  sortedEvents.forEach(event => {
    const value = readValue(event);
    if (typeof value !== 'number' || !Number.isFinite(value)) return;

    const fullSecond = eventToRelativeSecond(event.timestamp, originTimestamp);
    const bucket = groupedByFullSecond.get(fullSecond) ?? [];
    bucket.push(value);
    groupedByFullSecond.set(fullSecond, bucket);
  });

  const completeTrack = new Map(
    [...groupedByFullSecond.entries()].map(([second, values]) => [
      second,
      values.reduce((sum, value) => sum + value, 0) / values.length,
    ])
  );

  const segmentedEntries = [...completeTrack.entries()].flatMap(
    ([fullSecond, value]) => {
      if (segment === 'pre') {
        return fullSecond < baselineWindowSeconds
          ? [[fullSecond, value] as const]
          : [];
      }

      return fullSecond >= baselineWindowSeconds
        ? [[fullSecond - baselineWindowSeconds, value] as const]
        : [];
    }
  );

  return new Map(segmentedEntries);
};

export const getEffectiveHeartRateData = (session: {
  hrData?: unknown;
  hrData_repaired?: unknown;
}): HeartRateEvent[] => {
  const repaired = parseHeartRateData(session.hrData_repaired);
  if (repaired.length > 0) return repaired;
  return parseHeartRateData(session.hrData);
};
